// Copyright © 2021 Move Closer

import { Component, Prop } from 'vue-property-decorator'
import { HasDriver } from '@d24/demarked'

import { CreatesPdf, Related, SharesContent, ShareType } from '../../services'

import { AbstractModuleUi, FetchesRelatedAsync } from '../_abstract/ui'
import {
  Addon,
  AddonData,
  AppTheme,
  AnyObject,
  Author,
  ContentMeta,
  Image,
  ImageFile,
  ImageRatio,
  ModuleDriver,
  VideoFile
} from '../_contracts'
import {
  ArticleActions,
  ArticleAuthor,
  ArticleHeaderData,
  ArticleShare,
  ArticleSocials,
  ImageWithCaption, LineHeader, PlayIcon
} from '../_components/ui'

import { ArticleTeaserModule } from './ArticleTeaser.contracts'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { prepareImage } from '../_filters'
import { IArticleAction } from '../_components/ArticleActions/ArticleAction'
import ArticleHeaderVideo from '../_components/ArticleHeader/ArticleHeaderVideo/ArticleHeaderVideo'
import { BootstrapButton, BootstrapIcon, BootstrapImage, BootstrapLink } from '../../dsl/bootstrap'
import { Gallery } from '../_components/Gallery'
import NoSSR from 'vue-no-ssr'
import VueEasyLightbox from 'vue-easy-lightbox'
import { AdBannerModuleUi } from '../AdBanner/AdBanner.ui'

/**
 * Component capable of rendering the `ArticleTeaser's` contents.
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
@Component<ArticleTeaserModuleUi>({
  name: 'ArticleTeaserModuleUi',
  components: {
    ArticleActions,
    ArticleAuthor,
    ArticleHeaderVideo,
    ArticleShare,
    ArticleSocials,
    BootstrapButton,
    BootstrapIcon,
    BootstrapImage,
    BootstrapLink,
    Gallery,
    ImageWithCaption,
    LineHeader,
    NoSSR,
    PlayIcon,
    VueEasyLightbox,
    AdBannerModuleUi
  },
  mounted (): void {
    this.watchEvents()
  },
  async created () {
    const parent = this.parent
    const cover = this.parent.hasProperty(Addon.Cover) ? this.parent.getProperty(Addon.Cover) : undefined
    const videoSettings = this.module.content.videoSettings || {}
    const theme = this.module.content.theme || null
    const ads = this.module.content.ads || {}
    this.headerData = {
      author: {} as Author, // not used in ArticleTeaser.ui.ts
      header: null,
      categoryUrl: parent.get('categoryUrl'),
      ads: ads,
      theme: theme,
      cover: {
        image: null, // not used in ArticleTeaser.ui.ts
        video: null, // not used in ArticleTeaser.ui.ts
        videoSettings,
        gallery: null, // not used in ArticleTeaser.ui.ts
        hideDescription: typeof cover === 'object' && cover !== null ? (cover.hideDescription || false) : false
      },
      lead: parent.hasProperty(Addon.Lead) ? this.parseLead(parent.getProperty(Addon.Lead) as string) : null,
      meta: parent.getProperty(Addon.Meta) as ContentMeta,
      options: {
        hasPaywall: parent.get<AnyObject>('properties').paywall,
        hidePublicationDate: this.canHidePublicationDate ? parent.getOption('hidePublicationDate') : false
      },
      contentProperties: {
        canonicalUrl: parent.get<AnyObject>('properties').canonicalUrl,
        site: parent.get<AnyObject>('properties').site,
        videoAdsTag: parent.get<AnyObject>('properties').videoAdsTag
      },
      parent: parent.get('parent'),
      pdf: parent.get('pdf'),
      publicationDate: parent.get('publicationDate'),
      title: parent.get('title'),
      urlPath: parent.get('urlPath')
    }

    this.maxWidth = this.isMobile ? 768 : 800
    if (this.headerData.theme === AppTheme.PremiumZone) {
      this.maxWidth = this.isMobile ? 768 : 1220
    }
    await this.fetchRelated()
  },

  template: `
    <header class="article-header" :class="{ '--loaded': !isLoading }">


    <!-- Theme: PremiumZone TODO: -->
    <template v-if="getHeaderData.theme === AppTheme.PremiumZone">
      <div class="row justify-content-center">
        <div class="col-12 col-md-10">
          <!-- Article's title -->
          <h1 class="article-header__title fs-3 fs-lg-1 text-md-center">
            {{ getHeaderData.title }}
          </h1>

          <div class="row justify-content-between">
            <!-- Author -->
            <div class="col-auto">
              <div v-if="authorLink">
                <BootstrapLink :link="{ target: authorLink }" class="text-decoration-none">
                  <ArticleAuthor v-bind="{ publicationDate: getHeaderData.publicationDate }"
                                :hideDate="getHeaderData.options && getHeaderData.options.hidePublicationDate"
                                :name="authorName"
                                :avatar="authorAvatar !== null ? authorAvatar : undefined"
                                :showInitialsWhenNoAvatar="true"/>
                </BootstrapLink>
              </div>
              <div v-else>
                <ArticleAuthor v-bind="{ publicationDate: getHeaderData.publicationDate }"
                              :hideDate="getHeaderData.options && getHeaderData.options.hidePublicationDate"
                              :name="authorName"
                              :avatar="authorAvatar !== null ? authorAvatar : undefined"
                              :showInitialsWhenNoAvatar="true"/>
              </div>
            </div>

            <!-- Print, download & social buttons -->
            <div class="col-auto d-flex justify-content-between align-items-center">
              <ArticleActions class="me-lg-3" v-bind="{ actions }"/>
              <ArticleSocials class="d-none d-lg-block" v-bind="{ enabledServices }"
                              :onClick="onSocialClick"/>
            </div>
          </div>
        </div>

        <!-- Cover image -->
        <div class="col-12">
          <div class="article-header__image">
            <ImageWithCaption v-if="hasThumbnail" :image="thumbnail" :isLazy="isLazy"
                              :fetchPriority="!isLazy"
                              class="article-header__image__cover --fixed-ratio --21x9">
              <ArticleHeaderVideo v-if="hasVideo"
                                  :video="video"
                                  :videoSettings="getHeaderData.cover.videoSettings"
                                  :videoAdsTag="videoAdsTag"
                                  :noThumbnail="thumbnail === null"
                                  :baseVideoMetadata="baseVideoMetadata"
                                  :eventBus="eventBus"
                                  :siteDomain="getHeaderData.contentProperties.site.address"/>
            </ImageWithCaption>
          </div>
        </div>
        <!-- END | Cover image -->

        <!-- Article's lead -->
        <div v-if="getHeaderData.lead" v-html="getHeaderData.lead" class="col-12 col-md-8 article-header__lead"/>
      </div>

    </template>
    <!-- END | Theme: PremiumZone TODO: -->

    <!-- Theme: Default or other sites -->
    <template v-else>
      <template v-if="hasVideo">
        <div class="article-header__wrap">
          <!--Cover image / Single image -->
          <div v-if="hasThumbnail || fallbackImage" class="article-header__image">
            <template v-if="hasThumbnail">
              <ImageWithCaption v-if="thumbnail" :image="thumbnail"
                                :hideDescription="true" :isLazy="isLazy"
                                :hideAuthor="true"
                                :fetchPriority="!isLazy"
                                @click="openFullScreenPreview">
                <ArticleHeaderVideo :video="video"
                                    :videoSettings="getHeaderData.cover.videoSettings"
                                    :videoAdsTag="videoAdsTag"
                                    :noThumbnail="thumbnail === null"
                                    :baseVideoMetadata="baseVideoMetadata"
                                    :eventBus="eventBus"
                                    :siteDomain="getHeaderData.contentProperties.site.address"/>
                <AdBannerModuleUi v-if="getHeaderData.ads" class="article-header__ad"
                                  v-bind="{eventBus, fallbackImage, onEmptyCallback: emptyAds, module: getHeaderData.ads, parent, pdfService, shareService, theme, windowService, isMobile, relatedService, forceHide: forceHideAd}"/>

              </ImageWithCaption>
            </template>
            <template v-else>
              <BootstrapImage :src="fallbackImage"/>
              <ArticleHeaderVideo :video="video"
                                  :videoSettings="getHeaderData.cover.videoSettings"
                                  :videoAdsTag="videoAdsTag"
                                  :noThumbnail="thumbnail === null"
                                  :baseVideoMetadata="baseVideoMetadata"
                                  :eventBus="eventBus"
                                  :siteDomain="getHeaderData.contentProperties.site.address"/>
              <AdBannerModuleUi v-if="getHeaderData.ads" class="article-header__ad"
                                v-bind="{eventBus, fallbackImage, onEmptyCallback: emptyAds, module: getHeaderData.ads, parent, pdfService, shareService, theme, windowService, isMobile, relatedService, forceHide: forceHideAd}"/>
            </template>
            <NoSSR>
              <VueEasyLightbox v-if="!hasVideo && thumbnail"
                               :visible="isFullScreenPreviewOpen" :imgs="thumbnail.fullscreen"
                               @hide="closeFullScreenPreview"/>
            </NoSSR>
          </div>
          <!-- END | Cover image -->
          <!-- Title -->
          <h1 class="article-header__title fs-3 fs-lg-1">{{ getHeaderData.title }}</h1>
        </div>
        <div class="row justify-content-between mt-5 mb-4">
          <!-- Author -->
          <div class="col-auto">
            <div v-if="authorLink">
              <BootstrapLink :link="{ target: authorLink }" class="text-decoration-none">
                <ArticleAuthor v-bind="{ publicationDate: getHeaderData.publicationDate }"
                               :hideDate="getHeaderData.options && getHeaderData.options.hidePublicationDate"
                               :name="authorName"
                               :avatar="authorAvatar !== null ? authorAvatar : undefined"
                               :showInitialsWhenNoAvatar="true"/>
              </BootstrapLink>
            </div>
            <div v-else>
              <ArticleAuthor v-bind="{ publicationDate: getHeaderData.publicationDate }"
                             :hideDate="getHeaderData.options && getHeaderData.options.hidePublicationDate"
                             :name="authorName"
                             :avatar="authorAvatar !== null ? authorAvatar : undefined"
                             :showInitialsWhenNoAvatar="true"/>
            </div>
          </div>

          <!-- Print, download & social buttons -->
          <div class="col-auto d-flex justify-content-between align-items-center">
            <ArticleActions class="me-lg-3" v-bind="{ actions }"/>
            <ArticleSocials class="d-none d-lg-block" v-bind="{ enabledServices }"
                            :onClick="onSocialClick"/>
          </div>
        </div>
        <ArticleShare v-bind="{ shareButtons }" :isHidden="false"/>
        <div v-if="getHeaderData.lead" v-html="getHeaderData.lead" class="article-header__lead"/>

      </template>
      <template v-else>
        <!-- Title -->
        <h1 class="article-header__title fs-3 fs-lg-1">{{ getHeaderData.title }}</h1>

        <div class="row justify-content-between">
          <!-- Author -->
          <div class="col-auto">
            <div v-if="authorLink">
              <BootstrapLink :link="{ target: authorLink }" class="text-decoration-none">
                <ArticleAuthor v-bind="{ publicationDate: getHeaderData.publicationDate }"
                               :hideDate="getHeaderData.options && getHeaderData.options.hidePublicationDate"
                               :name="authorName"
                               :avatar="authorAvatar !== null ? authorAvatar : undefined"
                               :showInitialsWhenNoAvatar="true"/>
              </BootstrapLink>
            </div>
            <div v-else>
              <ArticleAuthor v-bind="{ publicationDate: getHeaderData.publicationDate }"
                             :hideDate="getHeaderData.options && getHeaderData.options.hidePublicationDate"
                             :name="authorName"
                             :avatar="authorAvatar !== null ? authorAvatar : undefined"
                             :showInitialsWhenNoAvatar="true"/>
            </div>
          </div>

          <!-- Print, download & social buttons -->
          <div class="col-auto d-flex justify-content-between align-items-center">
            <ArticleActions class="me-lg-3" v-bind="{ actions }"/>
            <ArticleSocials class="d-none d-lg-block" v-bind="{ enabledServices }"
                            :onClick="onSocialClick"/>
          </div>
        </div>
        <div class="article-header__wrap">
          <!--Cover image / Gallery -->
          <template v-if="hasGallery">
            <Gallery :images="images" class="article-header__gallery" :maxWidth="maxWidth"
                     :windowService="windowService"
                     fullScreen/>
          </template>
          <!-- END | Cover image / Gallery -->
          <!--Cover image / Single image -->
          <template v-else>
            <div v-if="hasThumbnail || fallbackImage" class="article-header__image">
              <template v-if="hasThumbnail">
                <ImageWithCaption v-if="thumbnail" :image="thumbnail" :overridenCaption="overridenCaption"
                                  :hideDescription="shouldHideDescription" :isLazy="isLazy"
                                  :fetchPriority="!isLazy"
                                  @click="openFullScreenPreview">
                  <AdBannerModuleUi v-if="getHeaderData.ads" class="article-header__ad"
                                    v-bind="{eventBus, fallbackImage, onEmptyCallback: emptyAds, module: getHeaderData.ads, parent, pdfService, shareService, theme, windowService, isMobile, relatedService, forceHide: forceHideAd}"/>

                </ImageWithCaption>
              </template>
              <template v-else>
                <BootstrapImage :src="fallbackImage"/>
                <AdBannerModuleUi v-if="getHeaderData.ads" class="article-header__ad"
                                  v-bind="{eventBus, fallbackImage, onEmptyCallback: emptyAds, module: getHeaderData.ads, parent, pdfService, shareService, theme, windowService, isMobile, relatedService, forceHide: forceHideAd}"/>
              </template>

              <NoSSR>
                <VueEasyLightbox v-if="!hasVideo && thumbnail"
                                 :visible="isFullScreenPreviewOpen" :imgs="thumbnail.fullscreen"
                                 @hide="closeFullScreenPreview"/>
              </NoSSR>
            </div>
          </template>
          <!-- END | Cover image -->
        </div>
        <ArticleShare v-bind="{ shareButtons }" :isHidden="false"/>
        <div v-if="getHeaderData.lead" v-html="getHeaderData.lead" class="article-header__lead"/>
        <div v-else></div>
      </template>
    </template>
    <!-- END | Theme: Default or other sites -->

    <component :is="'script'" type="application/ld+json"
               v-html="JSON.stringify(contentMetadata)"/>
    </header>
  `
})
export class ArticleTeaserModuleUi
  extends AbstractModuleUi<ArticleTeaserModule>
  implements FetchesRelatedAsync {
  /**
   *  Service responsible for printing PDFs.
   */
  @Prop({
    type: Object,
    required: false,
    default: null
  })
  public readonly pdfService!: CreatesPdf

  /**
   * Service capable of sharing the passed-in data using different mediums.
   */
  @Prop({
    type: Object,
    required: true
  })
  public readonly shareService!: SharesContent

  public readonly AppTheme = AppTheme
  public image: ImageFile | null = null;
  public author: Author | null = null;

  /**
   * Social channels available in the article.
   */
  public enabledServices: string[] = ['facebook', 'twitter', 'linkedin', 'instagram']
  /**
   * Article's thumbnail.
   */
  public thumbnail: Image | null = null;
  public maxWidth: number | undefined = undefined

  /**
   * Article author's avatar.
   */
  public authorAvatar: Image | null = null;

  public gallery: ImageFile[] | null = null;

  public headerData: ArticleHeaderData = {} as ArticleHeaderData
  /**
   * Determines whether image is displayed in full screen mode
   */
  public isFullScreenPreviewOpen: boolean = false

  /**
   * Article author's full name.
   */
  public authorName: string = ''

  public forceHideAd: boolean = false

  public video = {
    file: undefined,
    playOnTile: false,
    url: '',
    onNetworkUrl: ''
  } as ArticleHeaderData['cover']['video']

  public get getHeaderData () {
    return this.headerData
  }

  public get resAuthor (): Author {
    return this.author as Author
  }

  public watchEvents () {
    if (!this.eventBus) {
      return
    }

    this.eventBus.handle('ui:video-player.play', this.hideAds)
  }

  public hideAds () {
    this.forceHideAd = true
  }

  public get canHidePublicationDate (): boolean {
    if (this.parent!.getOption('hidePublicationDate') && typeof this.parent!.getOption('hidePublicationDate') === 'boolean') {
      return true
    }
    return false
  }

  private async fetchImage (cover: AddonData): Promise<void> {
    let image = null
    try {
      image = typeof cover === 'object' && cover !== null &&
      typeof cover.image === 'object' && cover.image !== null && !!cover.image.value
        ? await this.relatedService.resolve(cover.image) as ImageFile : null
    } catch (e) {
      image = null
    }

    // Overwriting image data by CoverAddon data
    if (image && typeof cover === 'object' && cover !== null) {
      const shouldOverride = typeof cover.override === 'undefined' ? false : cover.override

      if (shouldOverride && typeof cover.caption === 'string') {
        image.caption = cover.caption
      }
      if (shouldOverride && typeof cover.alt === 'string') {
        image.alt = cover.alt
      }
      if (shouldOverride && typeof cover.imageAuthor === 'string') {
        image.author = cover.imageAuthor
      }
    }
    this.image = image as ImageFile
    this.thumbnail = prepareImage(this.image, ImageRatio.Standard169, this.maxWidth, this.isLazy)
  }

  private async fetchAuthor (author: Related) {
    try {
      this.author = await this.relatedService.resolve(author) as Author
      if (this.author.avatar) {
        this.authorAvatar = prepareImage(this.author.avatar, ImageRatio.Square, 28, this.isLazy)
      }
      this.authorName = `${this.author.firstName} ${this.author.lastName}`
    } catch (e) {
      console.warn(e)
    }
  }

  private async fetchVideo (cover: AddonData) {
    let video = null
    try {
      if (typeof cover !== 'object' || cover === null) {
        video = null
      } else {
        const videoFile = typeof cover.video === 'object' && cover.video !== null &&
        typeof cover.video.file === 'object' && !!cover.video.file.value
          ? await this.relatedService.resolve(cover.video.file) as VideoFile : null
        video = {
          ...cover.video
          // file: videoFile
        }
      }
    } catch (e) {
      video = null
    }
    this.video = video
  }

  private async fetchGallery (cover: AddonData) {
    let gallery = null
    try {
      if (
        typeof cover === 'object' &&
        cover !== null &&
        typeof cover.gallery === 'object' &&
        cover.gallery !== null
      ) {
        const imageCollection = await this.relatedService.resolve(cover.gallery) as AnyObject
        gallery = (Array.isArray(imageCollection) ? imageCollection : [imageCollection]) as ImageFile[]
      } else {
        gallery = null
      }
    } catch (e) {
      gallery = null
    }
    this.gallery = gallery
  }

  /**
   * Checking that content has gallery
   */
  public get hasGallery (): boolean {
    return !!this.gallery
  }

  /**
   * Determines whether the article has its corresponding video.
   */
  public get hasVideo (): boolean {
    if (!this.video) {
      return false
    }

    const checkUrl = (typeof this.video.url === 'string' && this.video.url.length > 0)
    const checkFile = (this.video.file && typeof this.video.file === 'object' && (!!this.video.file.m3u8 || !!this.video.file.url)) === true
    const checkOn = (typeof this.video.onNetworkUrl === 'string' && this.video.onNetworkUrl.length > 0)
    return checkUrl || checkOn || checkFile
  }

  /**
   * @inheritDoc
   */
  public async fetchRelated (): Promise<void> {
    try {
      const parent = this.parent
      const cover = this.parent.hasProperty(Addon.Cover) ? this.parent.getProperty(Addon.Cover) : undefined
      await Promise.all([
        this.fetchImage(cover as AddonData),
        this.fetchVideo(cover as AddonData),
        this.fetchAuthor(this.parent.get('author')),
        this.fetchGallery(cover as AddonData)
      ])
      this.onReady()
    } catch (e) {
      this.onError(e)
    }
  }

  /**
   * Checking that content has cover photo - Thumbnail
   */
  public get hasThumbnail (): boolean {
    return !!this.thumbnail
  }

  public get contentMetadata () {
    dayjs.extend(utc)
    dayjs.extend(timezone)
    let metaImage = ''
    if (this.image) {
      metaImage = this.image.url
    }
    if (this.gallery && this.gallery[0]) {
      metaImage = this.gallery[0].url
    }

    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'NewsArticle',
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': this.headerData.contentProperties.canonicalUrl
          },
          headline: this.headerData.meta.title,
          articleSection: this.headerData.parent ? this.headerData.parent.name : '',
          description: this.headerData.meta.description,
          image: metaImage,
          datePublished: dayjs.tz(this.headerData.publicationDate, 'Europe/Warsaw').format('YYYY-MM-DDTHH:mm:ssZ'),
          dateModified: dayjs.tz(this.headerData.publicationDate, 'Europe/Warsaw').format('YYYY-MM-DDTHH:mm:ssZ'),
          author: [{
            '@type': 'Person',
            name: `${this.resAuthor.firstName} ${this.resAuthor.lastName}`,
            url: `https://${this.headerData.contentProperties.site.domain}${this.resAuthor.url}`
          }],
          inLanguage: this.headerData.contentProperties.site.locale,
          isAccessibleForFree: !this.headerData.options.hasPaywall,
          url: this.headerData.contentProperties.canonicalUrl,
          publisher: {
            '@id': `https://${this.headerData.contentProperties.site.domain}`,
            '@type': 'NewsMediaOrganization',
            name: this.headerData.contentProperties.site.name,
            logo: this.headerData.contentProperties.site.logo.light
          }
        }
      ]
    }
  }

  private parseLead (markup: string) {
    let html: string
    let obj: HasDriver<ModuleDriver.Paragraph>[]
    try {
      obj = this.DeMarked.convertMDToObjects(markup) as HasDriver<ModuleDriver.Paragraph>[]
      html = this.DeMarked.convertObjectsToHTML(obj)
      this.onReady()
      return html
    } catch (e) {
      this.onError(e)
      return ''
    }
  }

  public get isLazy () {
    return this.module.isLazyLoaded
  }

  public emptyAds = () => {
    console.log('Empty cover Ad')
  }

  public get videoAdsTag (): string | undefined {
    return this.headerData.contentProperties.videoAdsTag
  }

  /**
   * Closes full screen view of image
   */
  public closeFullScreenPreview (): void {
    this.isFullScreenPreviewOpen = false
  }

  /**
   * Opens image in the full screen mode
   */
  public openFullScreenPreview (): void {
    this.isFullScreenPreviewOpen = true
  }

  public get baseVideoMetadata (): AnyObject {
    let name = this.overridenCaption ? this.overridenCaption : (this.thumbnail ? this.thumbnail.caption : '')
    if (!name) {
      name = this.headerData.meta.title
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name,
      thumbnailUrl: this.thumbnail ? this.thumbnail.src : '',
      uploadDate: this.headerData.publicationDate
    }
  }

  /**
   * Checking if cover photo has an overriden caption
   */
  public get overridenCaption (): string | undefined {
    return (typeof this.headerData.cover === 'object' && this.headerData.cover.override === true) ? this.headerData.cover.caption : undefined
  }

  /**
   * Get images list to gallery.
   */
  public get images (): ImageFile[] {
    if (!this.gallery) {
      return []
    }

    return this.gallery
  }

  public get authorLink () {
    if (!this.author || !this.author.url) {
      return ''
    }
    return this.author.url
  }

  /**
   * TODO: Description.
   */
  public get shareButtons () {
    return [
      {
        callback: () => {
          this.onSocialClick(ShareType.Facebook)
        },
        icon: 'facebook',
        label: this.$t('share.facebook')
      },
      {
        callback: () => {
          this.onSocialClick(ShareType.Twitter)
        },
        icon: 'x',
        label: this.$t('share.twitter')
      },
      {
        callback: () => {
          this.onSocialClick(ShareType.LinkedIn)
        },
        icon: 'linkedin',
        label: this.$t('share.linkedin')
      },
      {
        callback: () => {
          return this.onSocialClick(ShareType.CopyLink)
        },
        clickedIcon: 'checkmark',
        icon: 'link',
        label: this.$t('share.link')
      },
      {
        callback: () => {
          this.onSocialClick(ShareType.Email)
        },
        clickedIcon: 'checkmark',
        icon: 'email',
        label: this.$t('share.email')
      }
    ]
  }

  /**
   * TODO: Description.
   */
  public get shareUrl () {
    if (typeof document === 'undefined') {
      return this.headerData.urlPath
    }

    if (!this.headerData.urlPath) {
      return document.location.origin + document.location.pathname
    }

    if (this.headerData.urlPath.indexOf('http') >= 0) {
      return this.headerData.urlPath
    }

    return document.location.origin + '/' + this.headerData.urlPath
  }

  /**
   * TODO: Description.
   */
  public onSocialClick (social: ShareType) {
    this.shareService.share(social, {
      url: this.shareUrl,
      meta: this.headerData.meta
    })
  }

  /**
   * Article actions.
   */
  public get actions (): IArticleAction[] {
    const actions: IArticleAction[] = [
      {
        callback: () => {
          window.print()
        },
        icon: 'print',
        label: 'Drukuj',
        hideForMobile: true
      }
    ]

    if (this.headerData.pdf && this.pdfService) {
      actions.push(
        {
          callback: () => {
            if (typeof window !== 'undefined' && this.pdfService && this.headerData.pdf) {
              const previewMatch = window.location.href.match(/\/preview\/([^/?#]+)/)
              const previewValue = previewMatch ? previewMatch[1] : null
              const pdfData = { ...this.headerData.pdf }

              if (previewValue) {
                pdfData.path = `${pdfData.path}?preview=${previewValue}`
              }

              return this.pdfService.downland(pdfData)
            }
          },
          disabled: !this.headerData.pdf.path,
          icon: 'download',
          label: 'PDF'
        }
      )
    }

    return actions
  }

  public get shouldHideDescription (): boolean {
    if (!this.headerData.cover.hideDescription) {
      return false
    }

    return this.headerData.cover.hideDescription
  }
}
